/**
 * Progress Bar Component
 * Visual representation of financial goal progress
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface ProgressBarProps {
  current: number;
  target: number;
  showAmounts?: boolean;
  height?: number;
}

export default function ProgressBar({
  current,
  target,
  showAmounts = true,
  height = 24,
}: ProgressBarProps) {
  // Calculate progress percentage (0-100)
  const percentage = target > 0 ? Math.min((current / target) * 100, 100) : 0;
  const roundedPercentage = Math.round(percentage);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `${amount.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}€`;
  };

  // Determine color based on progress
  const getProgressColor = (): string => {
    if (percentage >= 100) return '#4CAF50'; // Green - completed
    if (percentage >= 75) return '#8BC34A'; // Light green
    if (percentage >= 50) return '#FFC107'; // Yellow/orange
    if (percentage >= 25) return '#FF9800'; // Orange
    return colors.primary; // Blue - just started
  };

  const progressColor = getProgressColor();
  const isCompleted = percentage >= 100;

  return (
    <View style={styles.container}>
      {/* Progress bar */}
      <View style={[styles.progressBarContainer, { height }]}>
        <View
          style={[
            styles.progressBarFill,
            {
              width: `${percentage}%`,
              backgroundColor: progressColor,
              height,
            },
          ]}
        >
          {/* Percentage text inside bar if there's room (>15%) */}
          {percentage >= 15 && (
            <Text style={[styles.percentageTextInside, { lineHeight: height }]}>
              {roundedPercentage}%
            </Text>
          )}
        </View>

        {/* Percentage text outside bar if there's no room */}
        {percentage < 15 && percentage > 0 && (
          <Text style={[styles.percentageTextOutside, { lineHeight: height }]}>
            {roundedPercentage}%
          </Text>
        )}
      </View>

      {/* Amount labels */}
      {showAmounts && (
        <View style={styles.amountsContainer}>
          <Text style={styles.currentAmount}>
            {isCompleted ? '✅ ' : (current > 0 ? '💰 ' : '📊 ')}
            {current > 0 ? formatCurrency(current) : 'Pas encore commencé'}
          </Text>
          <Text style={styles.targetAmount}>
            🎯 {formatCurrency(target)}
          </Text>
        </View>
      )}

      {/* Completion message */}
      {isCompleted && (
        <View style={styles.completionBadge}>
          <Text style={styles.completionText}>🎉 Objectif atteint !</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginVertical: spacing.sm,
  },
  progressBarContainer: {
    width: '100%',
    backgroundColor: '#E0E0E0',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  progressBarFill: {
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'width 0.3s ease',
  },
  percentageTextInside: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    textAlign: 'center',
  },
  percentageTextOutside: {
    position: 'absolute',
    right: spacing.sm,
    color: colors.textSecondary,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
  },
  amountsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
    paddingHorizontal: spacing.xs,
  },
  currentAmount: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  targetAmount: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  completionBadge: {
    marginTop: spacing.sm,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    alignSelf: 'flex-start',
  },
  completionText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: '#2E7D32',
  },
});
